import { IncentivSigner, IncentivResolver, IncentivEnvironment } from '@incentiv/dapp-sdk';
import { ethers } from 'ethers';
import { 
  WrapperConfig, 
  ErrorCodes,
  ContractConfig,
  ContractEvent,
} from '../types';
import { createError } from '../utils';
import { ABIParser } from '../utils/abiParser';

/**
 * Simplified Incentiv dApp-Wrapper
 * Provides core blockchain functions and contract interaction
 */
export class IncentivWrapper {
  private config: WrapperConfig;
  private signer: IncentivSigner | null = null;
  private CONTRACTS: Record<string, any> = {};

  constructor() {
    // Use fixed network configuration
    this.config = {
      network: {
        portal: "https://testnet.incentiv.io",
        rpc: "https://rpc2.testnet.incentiv.io",
        chainId: 28802
      }
    };
  }

  // Re-export everything from official SDK for convenience
  static IncentivSigner = IncentivSigner;
  static IncentivResolver = IncentivResolver;
  static IncentivEnvironment = IncentivEnvironment;

  // Default Environment (can be overridden in config)
  static Environment = {
    Portal: "https://testnet.incentiv.io",      // Incentiv Portal URL
    RPC: "https://rpc2.testnet.incentiv.io"     // JSON‑RPC endpoint
  };

  // Chain configuration
  static CHAIN_CONFIG = {
    CHAIN_ID: 28802,
    RPC_URL: "https://rpc2.testnet.incentiv.io",
    PORTAL_URL: "https://testnet.incentiv.io"
  };

  /**
   * Connects to the wallet - triggers Incentiv Portal popup for authentication
   */
  async connect(): Promise<string> {
    try {

      // First, try to get an existing account (if user is already connected)
      let address: string;
      
      try {
        address = await IncentivResolver.getAccountAddress(this.config.network.portal);
      } catch (error) {
        // No existing connection found, will trigger new connection below
        address = '';
      }

      // If no existing connection, trigger the connect flow
      if (!address) {

        // Create provider
        const provider = new ethers.providers.StaticJsonRpcProvider(this.config.network.rpc);

        // Create a signer without address to trigger the authentication flow
        // The Incentiv Portal should prompt the user to connect their wallet
        const tempSigner = new IncentivSigner({
          address: '', // Empty - portal will fill this after authentication
          provider,
          environment: this.config.network.portal,
          entryPoint: "0x5FF137D4b0FDCD49DcA30c7CF57E578a026d2789"
        });

        // Try to trigger authentication by attempting to get the address
        // This should open the Incentiv Portal popup for user authentication
        try {
          // Method 1: Try getAddress() which should trigger portal
          if (tempSigner.getAddress) {
            address = await tempSigner.getAddress();
          } else {
            // Method 2: Try getAccountAddress again after creating signer
            address = await IncentivResolver.getAccountAddress(this.config.network.portal);
          }
          
          if (!address) {
            throw createError(ErrorCodes.WALLET_NOT_CONNECTED, 'No wallet address received after authentication attempt');
          }
        } catch (authError: any) {
          if (authError.code === 'ACTION_REJECTED' || authError.message?.includes('rejected')) {
            throw createError(ErrorCodes.USER_REJECTED, 'User rejected wallet connection');
          }
          throw createError(ErrorCodes.WALLET_NOT_CONNECTED, 'Failed to authenticate with Incentiv Portal', authError);
        }
      }

      // Create provider
      const provider = new ethers.providers.StaticJsonRpcProvider(this.config.network.rpc);

      // Create the actual signer with the authenticated address
      this.signer = new IncentivSigner({
        address,
        provider,
        environment: this.config.network.portal,
        entryPoint: "0x5FF137D4b0FDCD49DcA30c7CF57E578a026d2789"
      });


      return address;
    } catch (error: any) {
      console.error('❌ Wallet connection failed:', error);
      
      // Handle specific error types
      if (error.code === ErrorCodes.USER_REJECTED) {
        throw error;
      }
      
      throw createError(
        ErrorCodes.WALLET_NOT_CONNECTED, 
        'Error during wallet connection', 
        error
      );
    }
  }

  /**
   * Forces a fresh wallet connection (useful if connection is stale)
   */
  async connectFresh(): Promise<string> {
    // Reset current signer
    this.signer = null;
    
    // Force fresh connection
    return await this.connect();
  }


  /**
   * Registers multiple contracts from configuration
   * @param contracts Array of contract configurations
   */
  public registerContracts(contracts: ContractConfig[]): void {
    for (const contract of contracts) {
      this.registerContract(contract.name, contract.address, contract.abi);
    }
  }

  /**
   * Executes a contract method - sends transaction for state-changing methods, calls for view/pure methods
   */
  private async executeContractMethod(
    contractAddress: string,
    contractInterface: ethers.utils.Interface,
    methodName: string,
    args: any[],
    stateMutability: string,
    etherValue: string = '0'
  ): Promise<any> {
    try {
      // Encode the calldata
      const calldata = contractInterface.encodeFunctionData(methodName, args);

      // For view/pure functions, use call (read-only) - no signer required
      if (stateMutability === 'view' || stateMutability === 'pure') {
        const provider = this.getProvider();
        const result = await provider.call({ 
          to: contractAddress, 
          data: calldata 
        });
        
        // Decode the result
        const decoded = contractInterface.decodeFunctionResult(methodName, result);
        return decoded.length === 1 ? decoded[0] : decoded;
      }

      // For state-changing functions (nonpayable/payable), signer is required
      if (!this.signer) {
        throw createError(ErrorCodes.WALLET_NOT_CONNECTED, 'Wallet connection required for state-changing transactions');
      }

      // Send transaction for state-changing functions
      const parsedValue = etherValue === '0' ? '0' : ethers.utils.parseEther(etherValue).toString();
      
      const txRequest = {
        to: contractAddress,
        data: calldata,
        value: parsedValue
      };

      const txResponse = await (await this.signer.sendTransaction(txRequest)).wait();

      return {
        hash: txResponse.transactionHash,
        receipt: txResponse
      };
    } catch (error: any) {
      // Specific error handling
      if (error.code === 'INSUFFICIENT_FUNDS') {
        throw createError(ErrorCodes.INSUFFICIENT_FUNDS, 'Insufficient funds');
      } else if (error.code === 'ACTION_REJECTED') {
        throw createError(ErrorCodes.USER_REJECTED, 'Transaction rejected by user');
      }
      
      throw createError(
        ErrorCodes.NETWORK_ERROR, 
        `Error executing method '${methodName}'`, 
        error
      );
    }
  }

  /**
   * Creates a contract instance with encoded function calls
   * Now uses the enhanced ABIParser for comprehensive ABI support
   */
  private createContractInstance(_name: string, address: string, abi: any[]): any {
    const contractInterface = new ethers.utils.Interface(abi);
    
    // Parse ABI using the enhanced parser
    const parsedABI = ABIParser.parseABI(abi);
    
    const contractInstance: any = {
      address,
      abi,
      interface: contractInterface,
      // Store parsed ABI information for easy access
      _parsedABI: parsedABI,
      // Store ABI summary for inspection
      _abiSummary: ABIParser.getABISummary(abi)
    };

    // Generate methods for each function in the ABI
    // Using the enhanced parser to ensure all functions are detected
    const functions = ABIParser.extractFunctions(abi);
    
    for (const func of functions) {
      const methodName = func.name;
      
      // Create method that returns execution object
      contractInstance[methodName] = (...args: any[]) => {
        const executionObject = {
          // Direct execution without value
          then: (resolve?: (value: any) => any, reject?: (reason?: any) => any) => {
            return this.executeContractMethod(address, contractInterface, methodName, args, func.stateMutability, '0').then(resolve, reject);
          },
          
          // Method to set ETH value for payable functions
          withValue: (etherValue: string | number) => {
            // Validate that the function is payable
            if (func.stateMutability !== 'payable') {
              throw createError(
                ErrorCodes.NETWORK_ERROR,
                `Cannot use withValue() on function '${methodName}' with stateMutability '${func.stateMutability}'. Only 'payable' functions accept ETH value.`
              );
            }
            
            // Validate ETH value
            const valueStr = etherValue.toString();
            if (isNaN(Number(valueStr)) || Number(valueStr) < 0) {
              throw createError(
                ErrorCodes.NETWORK_ERROR,
                `Invalid ETH value '${valueStr}'. Value must be a positive number.`
              );
            }
            
            return this.executeContractMethod(address, contractInterface, methodName, args, func.stateMutability, valueStr);
          }
        };

        // Make it awaitable by default (without withValue)
        Object.setPrototypeOf(executionObject, Promise.prototype);
        
        return executionObject;
      };
    }

    return contractInstance;
  }

  /**
   * Registers a new contract at runtime
   */
  public registerContract(name: string, address: string, abi: any[]): void {
    // Validate ABI before registration
    const validationErrors = ABIParser.validateABI(abi);
    if (validationErrors.length > 0) {
      throw createError(
        ErrorCodes.NETWORK_ERROR,
        `Invalid ABI for contract '${name}': ${validationErrors.join(', ')}`
      );
    }

    this.CONTRACTS[name] = this.createContractInstance(name, address, abi);
  }

  // ==================== ENHANCED ABI METHODS (PRIVATE) ====================
 

  /**
   * Creates a new signer instance
   */
  public createSigner(address: string): IncentivSigner {
    const provider = new ethers.providers.StaticJsonRpcProvider(this.config.network.rpc);
    
    return new IncentivSigner({
      address,
      provider,
      environment: this.config.network.portal,
      entryPoint: "0x9b5d240EF1bc8B4930346599cDDFfBD7d7D56db9"
    });
  }



  /**
   * Checks if a connection exists
   */
  public async isConnected(): Promise<boolean> {
    if (!this.signer) {
      return false;
    }

    try {
      const address = await IncentivResolver.getAccountAddress(this.config.network.portal);
      return !!address;
    } catch {
      return false;
    }
  }

  // ==================== CORE API METHODS ====================

  /**
   * Returns the current user address
   */
  public getUserAddress(): string | null {
    return this.signer?.address || null;
  }

  /**
   * Returns the provider
   */
  public getProvider(): ethers.providers.JsonRpcProvider {
    return new ethers.providers.StaticJsonRpcProvider(this.config.network.rpc);
  }

  /**
   * Returns the signer
   */
  public getSigner(): IncentivSigner | null {
    return this.signer;
  }


  // ==================== SIMPLIFIED PUBLIC API ====================

  /**
   * Get list of all registered contract names
   */
  public getRegisteredContracts(): string[] {
    return Object.keys(this.CONTRACTS);
  }

  /**
   * Check if a contract is registered
   */
  public hasContract(name: string): boolean {
    return !!this.CONTRACTS[name];
  }

  /**
   * Get contract by name
   */
  public getContract(name: string): any | null {
    return this.CONTRACTS[name] || null;
  }

  /**
   * Get contract address by name
   */
  public getContractAddress(name: string): string | null {
    return this.CONTRACTS[name]?.address || null;
  }

  /**
   * Get all function names for a contract (simplified version)
   */
  public getContractMethods(name: string): string[] | null {
    const contract = this.CONTRACTS[name];
    if (!contract) return null;

    const functions = ABIParser.extractFunctions(contract.abi);
    return functions.map(func => func.name);
  }

  /**
   * Get config
   */
  public getConfig(): WrapperConfig {
    return this.config;
  }

  /**
   * Get all events for a contract
   */
  public getContractEvents(name: string): ContractEvent[] | null {
    const contract = this.CONTRACTS[name];
    if (!contract) return null;

    return ABIParser.extractEvents(contract.abi);
  }
}
